import type { PoolOfferConfig } from '../../lib/api/operatorConsole';

/** Buyback the backend applies when a pack has no operator override. */
export const BUYBACK_DEFAULT = 0.9;

/** Highest RTP % a loot pack takes (the backend's platform_pack_max_rtp): above it a pack pays out more than it costs. */
export const MAX_LOOT_RTP = 100;

/** What the pricing table saves in one PATCH: an RTP (as any of its fields) or a buyback. */
export type PricingPatch = Partial<
  Pick<
    PoolOfferConfig,
    'estimated_value' | 'estimated_value_high' | 'estimated_value_max' | 'buyback_percent' | 'stated_rtp'
  >
>;

/** Volatility levels that can take their own RTP; Normal is the pack RTP itself. Labels as players see them. */
export const RTP_LEVELS = [
  { field: 'estimated_value_high', label: 'High' },
  { field: 'estimated_value_max', label: 'Max' },
] as const;

export type RtpLevel = (typeof RTP_LEVELS)[number];

/** Why a `restocking` pack is hidden, and what the operator can do about it. */
export const RESTOCKING_HINT =
  "Hidden from players: its cards can't cover the pack or its RTP right now. It comes back on its own once they can; a lower RTP can bring it back sooner.";

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function priceOf(c: PoolOfferConfig): number | null {
  return c.pool_offer_price != null && c.pool_offer_price > 0 ? c.pool_offer_price : null;
}

/** A custom pack is built by the player, so it has no pack price to measure RTP against. */
export function isCustomPack(c: PoolOfferConfig): boolean {
  return c.pool_offer_game_kind === 'custom_pack';
}

/** RTP % of an estimated value against the pack price; null without a price. */
function rtpOf(c: PoolOfferConfig, estimatedValue: number | null): number | null {
  const price = priceOf(c);
  if (price == null || estimatedValue == null) return null;
  return round2((estimatedValue / price) * 100);
}

/** RTP % of a custom pack's stated_rtp share; null when it is not set. */
function statedRtpPercent(share: number | null): number | null {
  return share == null ? null : round2(share * 100);
}

/** Operator's own RTP %; null when the pack inherits the default value. */
export function rtpOverride(c: PoolOfferConfig): number | null {
  return isCustomPack(c) ? statedRtpPercent(c.stated_rtp) : rtpOf(c, c.estimated_value);
}

/** RTP % the pack falls back to without an operator override. */
export function rtpDefault(c: PoolOfferConfig): number | null {
  return isCustomPack(c)
    ? statedRtpPercent(c.pool_offer_stated_rtp)
    : rtpOf(c, c.pool_offer_estimated_value);
}

/** RTP % in effect for the pack right now. */
export function rtpEffective(c: PoolOfferConfig): number | null {
  return rtpOverride(c) ?? rtpDefault(c);
}

/** Estimated value for an RTP %, rounded like the backend's global apply. */
export function estimatedValueFromRtp(c: PoolOfferConfig, rtp: number): number | null {
  const price = priceOf(c);
  return price == null ? null : round2((price * rtp) / 100);
}

/** The PATCH that sets a pack to an RTP %: a custom pack stores the share, a loot pack the value. */
export function pricingPatchForRtp(c: PoolOfferConfig, rtp: number): PricingPatch {
  return isCustomPack(c)
    ? { stated_rtp: rtp / 100 }
    : { estimated_value: estimatedValueFromRtp(c, rtp) };
}

/** A level's own RTP %; null when the level plays the Normal RTP. */
export function levelRtpOverride(c: PoolOfferConfig, level: RtpLevel): number | null {
  return rtpOf(c, c[level.field]);
}

/** The PATCH that sets a level's RTP %; null hands the level back to the Normal RTP. */
export function pricingPatchForLevelRtp(c: PoolOfferConfig, level: RtpLevel, rtp: number | null): PricingPatch {
  const patch: PricingPatch = {};
  patch[level.field] = rtp == null ? null : estimatedValueFromRtp(c, rtp);
  return patch;
}

/** Volatility levels belong to loot packs with a price; a custom pack has a single RTP. */
export function canSetLevelRtp(c: PoolOfferConfig): boolean {
  return !isCustomPack(c) && priceOf(c) != null;
}

/** Price text; a custom pack is priced per purchase, not per pack. A zero price is a price: only a missing one is a dash. */
export function priceLabel(c: PoolOfferConfig): string {
  if (isCustomPack(c)) return 'Variable';
  return c.pool_offer_price == null ? '—' : formatMoney(c.pool_offer_price);
}

/** A loot pack's RTP is a share of its price; a custom pack's RTP stands alone. */
export function canSetRtp(c: PoolOfferConfig): boolean {
  return isCustomPack(c) || priceOf(c) != null;
}

/** Average card value of one pack at an RTP %; a custom pack has no pack to average over. */
export function avgCardValue(c: PoolOfferConfig, rtp: number | null): number | null {
  if (isCustomPack(c) || rtp == null) return null;
  return estimatedValueFromRtp(c, rtp);
}

export function buybackPercent(fraction: number): number {
  return round2(fraction * 100);
}

export function buybackEffective(c: PoolOfferConfig): number {
  return buybackPercent(c.buyback_percent ?? BUYBACK_DEFAULT);
}

/**
 * Compared at one decimal: per-pack rounding of the estimated value brings
 * 96% back as 95.99 on some prices, which is still "96%" to the operator.
 */
export function samePercent(a: number | null, b: number | null): boolean {
  if (a == null || b == null) return a === b;
  return Math.round(a * 10) === Math.round(b * 10);
}

export type CommonValue = {
  /** Value most packs use; null when no pack has one. */
  value: number | null;
  /** Packs whose value differs from it. */
  differing: number;
};

/** The value most packs share — the page's "default for all packs". */
export function commonValue(values: (number | null)[]): CommonValue {
  const known = values
    .filter((v): v is number => v != null)
    .map((v) => Math.round(v * 10) / 10);
  if (known.length === 0) return { value: null, differing: 0 };

  const counts = new Map<number, number>();
  for (const v of known) counts.set(v, (counts.get(v) ?? 0) + 1);
  let value = known[0]!;
  for (const [v, n] of counts) if (n > counts.get(value)!) value = v;
  return { value, differing: known.length - counts.get(value)! };
}

export function formatMoney(n: number): string {
  return `$${n.toFixed(2)}`;
}
